import type { CartClient, CartOutputDto } from "@sticker-giant/sg-frontend-common";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { identifyReturningCustomer } from "@/lib/analytics/segment";
import { useAuthClaimsStore } from "@/stores/useAuthClaimsStore";
import { useGlobalStore } from "@/stores/useGlobalStore";
import { useServicesStore } from "@/stores/useServicesStore";
import { GuestIdHelper } from "@/utils/GuestIdHelper";

export const CART_QUERY_KEY = ["getCartItems"] as const;
// 5 minutes: mutations write the fresh server cart into the cache via setQueriesData (or
// invalidate in useRemoveCartItem's void-response case), identity changes refire the query via
// query-key membership, and refetchOnWindowFocus is off - so staleness can't mask correctness.
export const CART_STALE_TIME = 300_000;

// Target only the currently-subscribed cart query in mutations so we don't
// corrupt inactive cache entries belonging to a previous identity (e.g., post-logout).
export const CART_ACTIVE_FILTER = { queryKey: CART_QUERY_KEY, type: "active" as const };

export function commitCart(queryClient: QueryClient, cart: CartOutputDto | null): void {
  queryClient.setQueriesData<CartOutputDto | null>(CART_ACTIVE_FILTER, cart);
  queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY, type: "inactive" });
}

const UUID_PATTERN = /\/store\/checkout\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

function getCheckoutCartId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return UUID_PATTERN.exec(window.location.pathname)?.[1];
}

async function mergeGuestCart(
  cartClient: CartClient,
  sgCustomerId: string,
  guestId: string,
): Promise<CartOutputDto | null> {
  try {
    const mergedCart = await cartClient.mergeCart({
      sourceCustomerId: guestId,
      customer: { customerId: sgCustomerId, isLoggedIn: true },
    });
    if (!mergedCart) return null;

    GuestIdHelper.clearGuestId();
    // Traits only, no PII: useSyncSegmentIdentity identifies this same login with the
    // account's PII, and re-sending the cart's copy would put the same hashed
    // identifiers on the wire twice for one login.
    identifyReturningCustomer(mergedCart.customer.isReturningCustomer);
    return mergedCart;
  } catch {
    return null;
  }
}

async function resolveCart(
  cartClient: CartClient,
  sgCustomerId: string | undefined,
  checkoutCartId: string | undefined,
): Promise<CartOutputDto | null> {
  const guestId = GuestIdHelper.getGuestId();

  // A guest who logs in mid-checkout returns to /store/checkout/<guestCartId> still carrying their
  // guest ID. Merge it into the now-authenticated customer BEFORE honoring the URL cart ID below -
  // otherwise getCartById would re-fetch the guest cart (customer.guest still true) and trap them in
  // guest mode. The merge closes the guest cart and returns the customer cart (a new ID); the
  // checkout page syncs the URL to that ID.
  if (sgCustomerId && guestId) {
    const mergedCart = await mergeGuestCart(cartClient, sgCustomerId, guestId);
    if (mergedCart) return mergedCart;
  }

  if (checkoutCartId) {
    return await cartClient.getCartById(checkoutCartId);
  }

  const customerId = sgCustomerId || guestId;
  if (customerId) {
    // Cart-service returns 4xx when the customer has no OPEN cart (e.g., right after a
    // successful checkout closed the previous one). Treat that as "no cart" rather than
    // bubbling a query error that surfaces in the UI as broken.
    try {
      return (await cartClient.getCartByCustomerId(customerId)) ?? null;
    } catch {
      return null;
    }
  }

  return null;
}

export const useCart = () => {
  const cartClient = useServicesStore(state => state.cartClient);
  const authResolved = useServicesStore(state => state.authResolved);
  const setCartId = useGlobalStore(state => state.setCartId);
  const sgCustomerId = useAuthClaimsStore(state => state.claims?.sg_customer_id);
  const checkoutCartId = getCheckoutCartId();

  return useQuery<CartOutputDto | null>({
    queryKey: [...CART_QUERY_KEY, sgCustomerId, checkoutCartId],
    queryFn: async () => {
      if (!cartClient) return null;
      try {
        const cart = await resolveCart(cartClient, sgCustomerId, checkoutCartId);
        setCartId(cart?.id);
        return cart;
      } catch (error) {
        setCartId(undefined);
        throw error;
      }
    },
    enabled: !!cartClient && authResolved,
    staleTime: CART_STALE_TIME,
    refetchOnWindowFocus: false,
  });
};
